/**
 * Map / Locations API Client
 *
 * Purpose: Full CRUD for agent network map locations. Fixed locations (NamPost, ATM, warehouse)
 * are stored in the backend locations table; agent locations are read from the agents table.
 * Location: packages/api-client/src/ketchup/mapAPI.ts
 */

import { apiClient } from './api';

export type LocationType =
  | 'agent-pos'
  | 'nampost-office'
  | 'merchant-store'
  | 'atm'
  | 'warehouse'
  | 'mobile_unit';
export type LocationStatus = 'active' | 'inactive' | 'maintenance' | 'offline';

export interface MapLocation {
  id: string;
  name: string;
  type: LocationType;
  latitude: number;
  longitude: number;
  address?: string;
  region: string;
  status: LocationStatus;
  lastActive?: string;
  deviceId?: string;
  agentName?: string;
  contactNumber?: string;
  radiusKm?: number;
}

export const mapAPI = {
  /** Get locations for map. Optional type (e.g. mobile_unit for mobile portal), region. */
  async getAllLocations(params?: { type?: string; region?: string }): Promise<MapLocation[]> {
    const data = await apiClient.get<MapLocation[]>('/map/locations', params as Record<string, unknown>);
    return Array.isArray(data) ? data : [];
  },

  async getLocationById(id: string): Promise<MapLocation> {
    return apiClient.get<MapLocation>(`/map/locations/${id}`);
  },

  /** Create a fixed location (nampost-office, atm, warehouse). Backend: POST /map/locations. */
  async addLocation(location: Omit<MapLocation, 'id'>): Promise<MapLocation> {
    return apiClient.post<MapLocation>('/map/locations', location);
  },

  /** Update a fixed location. Backend: PUT /map/locations/:id. Agents are read-only. */
  async updateLocation(id: string, updates: Partial<MapLocation>): Promise<MapLocation> {
    return apiClient.put<MapLocation>(`/map/locations/${id}`, updates);
  },

  /** Delete a fixed location. Backend: DELETE /map/locations/:id. Agents are read-only. */
  async deleteLocation(id: string): Promise<void> {
    return apiClient.delete(`/map/locations/${id}`);
  },

  async getLocationsByRegion(region: string): Promise<MapLocation[]> {
    return apiClient.get<MapLocation[]>(`/map/locations/region/${encodeURIComponent(region)}`);
  },

  async getLocationsByType(type: string): Promise<MapLocation[]> {
    return apiClient.get<MapLocation[]>(`/map/locations/type/${encodeURIComponent(type)}`);
  },

  /** Fixed locations: NamPost offices, ATMs, warehouses (from locations table). */
  async getFixedLocations(params?: { type?: string; region?: string }): Promise<MapLocation[]> {
    const search = new URLSearchParams();
    if (params?.type) search.set('type', params.type);
    if (params?.region) search.set('region', params.region);
    const qs = search.toString();
    return apiClient.get<MapLocation[]>(`/map/locations/fixed${qs ? `?${qs}` : ''}`);
  },

  /** Bulk create fixed locations. Backend: POST /map/locations/import. Body: { locations: [...] }. */
  async importLocations(locations: Omit<MapLocation, 'id'>[]): Promise<{ imported: number }> {
    const data = await apiClient.post<{ imported: number }>('/map/locations/import', { locations });
    return data ?? { imported: 0 };
  },
};
